import { Sparkles } from 'lucide-react'
import type * as React from 'react'

import { AssistantGlyph } from '@/components/brand/AssistantGlyph'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'

/**
 * Visual emphasis for the Xendrel AI affordance:
 * - `solid`  — filled copper, for primary CTAs (empty-state "Generate" buttons)
 * - `neon`   — adaptive dark/light core with an aurora edge for the main AI trigger
 * - `subtle` — copper-tinted outline, the default toolbar look ("Ask Xendrel")
 * - `ghost`  — copper, hover-tinted background, for secondary triggers
 * - `link`   — text-only copper, for compact inline triggers inside prose
 */
type AiEmphasis = 'solid' | 'neon' | 'subtle' | 'ghost' | 'link'

const EMPHASIS: Record<
  AiEmphasis,
  { variant: React.ComponentProps<typeof Button>['variant']; className?: string }
> = {
  solid: { variant: 'solid' },
  neon: {
    variant: 'solid',
    className:
      'relative isolate overflow-hidden border-transparent bg-gradient-to-r from-cat-1 via-cat-6 to-cat-10 text-primary-foreground shadow-md shadow-cat-1/30 ring-1 ring-cat-10/30 hover:-translate-y-0.5 hover:brightness-110 hover:shadow-lg hover:shadow-cat-6/40 aria-pressed:ring-2 aria-pressed:ring-cat-10/60 aria-pressed:shadow-lg aria-pressed:shadow-cat-6/45 [&>svg]:relative [&>svg]:z-10 motion-reduce:hover:translate-y-0',
  },
  subtle: { variant: 'default' },
  ghost: { variant: 'ghost', className: 'text-primary hover:text-primary hover:bg-primary/10' },
  link: { variant: 'link' },
}

export type AiButtonProps = Omit<React.ComponentProps<typeof Button>, 'variant'> & {
  emphasis?: AiEmphasis
  /**
   * The button opens the Xendrel Assistant: it leads with the assistant's glyph,
   * where every other AI action ("Generate", the schema agent) keeps the sparkle.
   */
  assistant?: boolean
  /** Hide the leading icon (e.g. icon-only / custom layouts). */
  hideIcon?: boolean
}

/**
 * The single source of truth for the "Ask Xendrel" / "Generate" AI buttons.
 * Wraps {@link Button} with the leading AI glyph and a shared accent treatment
 * so every AI entry point stays visually consistent across the app.
 */
export function AiButton({
  emphasis = 'subtle',
  assistant = false,
  hideIcon = false,
  size = 'sm',
  className,
  children,
  loading,
  ...props
}: AiButtonProps) {
  const { variant, className: emphasisClassName } = EMPHASIS[emphasis]
  const neon = emphasis === 'neon'
  // The neon glyph grows on hover; the sparkle also tilts, the assistant's never does.
  const iconClassName = cn(
    neon && 'transition-transform group-hover/button:scale-110 motion-reduce:transition-none',
    neon && !assistant && 'group-hover/button:rotate-12'
  )
  return (
    <Button
      variant={variant}
      size={size}
      loading={loading}
      className={cn(emphasisClassName, className)}
      {...props}
    >
      {neon && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 -left-1/2 z-[1] w-1/3 skew-x-[-20deg] bg-gradient-to-r from-transparent via-primary-foreground/30 to-transparent transition-[left] duration-700 group-hover/button:left-[125%] motion-reduce:hidden"
        />
      )}
      {!hideIcon &&
        !loading &&
        (assistant ? (
          <AssistantGlyph
            data-icon="inline-start"
            // On a filled button the glyph takes the label's ink.
            ink={variant === 'solid' ? 'inherit' : 'foreground'}
            className={iconClassName}
          />
        ) : (
          <Sparkles data-icon="inline-start" className={iconClassName} />
        ))}
      {neon ? <span className="relative z-10">{children}</span> : children}
    </Button>
  )
}
